import { create } from 'zustand';
import { UndoCommand } from '../history/types';

const MAX_HISTORY = 60;

interface HistoryState {
  past:    UndoCommand[];
  future:  UndoCommand[];
  canUndo: boolean;
  canRedo: boolean;

  /**
   * The command that sat on top of `past` the last time the scene was saved —
   * the save point, marked in the history itself. `null` means "saved with an
   * empty stack", i.e. freshly loaded or undone all the way back to the start.
   *
   * Compared by identity, never by index: `past.length` at save time would be
   * wrong the moment MAX_HISTORY drops a command off the front, and it cannot
   * tell "undone back to the save point" (clean) from "undone past it and then
   * redone something else" (dirty).
   */
  savedAt: UndoCommand | null;
  /**
   * True when the stack has moved away from `savedAt` — the scene holds edits
   * the last save did not capture. Note this is only the part of "unsaved" that
   * the undo stack knows about; floorplan geometry and wall splits are tracked
   * separately (see utils/sceneDirtyState.ts, which ORs all three).
   */
  isDirty: boolean;

  /** Register a command that has already been executed by the caller. */
  push(cmd: UndoCommand): void;
  undo(): void;
  redo(): void;
  /** Call on scene load / reset to wipe history. */
  clear(): void;
  /** Call after a successful save to plant the save point at the current top. */
  markSaved(): void;
}

/**
 * A save that happens more than MAX_HISTORY commands ago has had its marker
 * sliced off the front of `past`, so the identity check can no longer find it
 * and every later state reads as dirty. That is the safe direction to be wrong
 * in: at worst the user is asked to confirm a discard they had nothing to
 * discard for.
 */
const isDirtySince = (past: UndoCommand[], savedAt: UndoCommand | null): boolean =>
  (past.length ? past[past.length - 1] : null) !== savedAt;

export const useHistoryStore = create<HistoryState>((set, get) => ({
  past:    [],
  future:  [],
  canUndo: false,
  canRedo: false,
  savedAt: null,
  isDirty: false,

  push(cmd) {
    set((s) => {
      const past = [...s.past, cmd].slice(-MAX_HISTORY);
      return { past, future: [], canUndo: true, canRedo: false, isDirty: isDirtySince(past, s.savedAt) };
    });
  },

  undo() {
    const { past } = get();
    if (!past.length) return;
    const cmd = past[past.length - 1];
    cmd.undo();
    set((s) => {
      const past = s.past.slice(0, -1);
      return {
        past,
        future:  [cmd, ...s.future],
        canUndo: s.past.length > 1,
        canRedo: true,
        isDirty: isDirtySince(past, s.savedAt),
      };
    });
  },

  redo() {
    const { future } = get();
    if (!future.length) return;
    const cmd = future[0];
    cmd.execute();
    set((s) => {
      const past = [...s.past, cmd];
      return {
        past,
        future:  s.future.slice(1),
        canUndo: true,
        canRedo: s.future.length > 1,
        isDirty: isDirtySince(past, s.savedAt),
      };
    });
  },

  clear() {
    // A wipe happens on scene load / reset, where the new starting point is by
    // definition what is on disk — so the save point resets with it.
    set({ past: [], future: [], canUndo: false, canRedo: false, savedAt: null, isDirty: false });
  },

  markSaved() {
    const { past } = get();
    set({ savedAt: past.length ? past[past.length - 1] : null, isDirty: false });
  },
}));
